import {
  EntityLinks,
  Goal,
  GoalLifeAreaLinkChange,
  GoalParentLinkChange,
  ID,
  RelationshipUpdate,
  UpdateGoal,
} from '@griply/types';
import { getDirectGoalLifeAreaIds, getGoalParentIds } from '@griply/utils';

import { buildGoalLinkWrites } from './build-goal-link-writes';
import { buildGoalParentLinkWrites } from './build-goal-parent-link-writes';

export type MergeGoalLinkChangesOptions = {
  updatedGoals: UpdateGoal[];
  goalParentChanges?: GoalParentLinkChange[];
  goalLifeAreaChanges?: GoalLifeAreaLinkChange[];
  goals: Goal[];
  links?: EntityLinks;
};

export type MergedGoalLinkChanges = {
  goalUpdates: UpdateGoal[];
  relationshipUpdates: RelationshipUpdate[];
};

// Folds link changes into the goal updates the caller already writes — the rules reject a 2nd write to a document within 100ms.
export const mergeGoalLinkChanges = ({
  updatedGoals,
  goalParentChanges = [],
  goalLifeAreaChanges = [],
  goals,
  links,
}: MergeGoalLinkChangesOptions): MergedGoalLinkChanges => {
  if (!goalParentChanges.length && !goalLifeAreaChanges.length) {
    return { goalUpdates: updatedGoals, relationshipUpdates: [] };
  }

  const goalsById = new Map<ID, Goal>(goals.map((goal) => [goal.id, goal]));
  const relationshipUpdates: RelationshipUpdate[] = [];
  const linkUpdatesById = new Map<ID, UpdateGoal>();
  const reparentedIds = new Set<ID>();

  const mergeLinkUpdate = (update: UpdateGoal | null) => {
    if (!update) {
      return;
    }
    linkUpdatesById.set(update.id, {
      ...linkUpdatesById.get(update.id),
      ...update,
    });
  };

  goalLifeAreaChanges.forEach(({ goalId, lifeAreaIds }) => {
    const goal = goalsById.get(goalId);
    if (!goal) {
      return;
    }
    const writes = buildGoalLinkWrites({
      goalId,
      lifeAreaIds,
      currentLifeAreaIds: getDirectGoalLifeAreaIds(goal, links),
      isMigrated: !!goal.lifeAreaLinksMigrated,
    });
    mergeLinkUpdate(writes.goalUpdate);
    relationshipUpdates.push(...writes.relationshipUpdates);
  });

  goalParentChanges.forEach(({ goalId, parentIds }) => {
    const goal = goalsById.get(goalId);
    if (!goal) {
      return;
    }
    const writes = buildGoalParentLinkWrites({
      goalId,
      parentIds,
      currentParentIds: getGoalParentIds(goal, { links }),
      isMigrated: !!goal.parentLinksMigrated,
    });
    if (writes.relationshipUpdates.length) {
      reparentedIds.add(goalId);
    }
    mergeLinkUpdate(writes.goalUpdate);
    relationshipUpdates.push(...writes.relationshipUpdates);
  });

  // A re-parent moves one link; the frozen legacy chain must not be rewritten alongside it.
  const merged = updatedGoals.map(({ ancestorIds, ...goal }) => ({
    ...goal,
    ...(reparentedIds.has(goal.id) || ancestorIds === undefined
      ? {}
      : { ancestorIds }),
    ...linkUpdatesById.get(goal.id),
  }));
  const mergedIds = new Set(merged.map(({ id }) => id));

  return {
    goalUpdates: [
      ...merged,
      ...Array.from(linkUpdatesById.values()).filter(
        ({ id }) => !mergedIds.has(id),
      ),
    ],
    relationshipUpdates,
  };
};
